/**
 * ProBros mobile drawer helpers:
 * - Own the summary click so native <details> + Horizon toggle() don't race
 * - Set .menu-open immediately and close reliably
 * - Close on same-page hash links so in-page nav isn’t trapped behind the drawer
 * - Restart staggered nav animations on every open
 * - Never use html scroll-lock (unlock was making the sticky header vanish on close)
 */

/**
 * @param {ParentNode} root
 */
function restartAnimatedElements(root) {
  root.querySelectorAll('.menu-drawer__animated-element').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.getAnimations().forEach((animation) => animation.cancel());
    el.style.animation = 'none';
    void el.offsetWidth;
    el.style.removeProperty('animation');
  });
}

function clearPageScrollLock() {
  document.documentElement.removeAttribute('scroll-lock');
}

/**
 * Same-document link that includes a hash (e.g. #events, /#partners).
 * @param {HTMLAnchorElement} anchor
 * @returns {boolean}
 */
function isSameDocumentHashLink(anchor) {
  const raw = anchor.getAttribute('href');
  if (!raw || !raw.includes('#')) return false;

  try {
    const url = new URL(anchor.href, window.location.href);
    return (
      url.origin === window.location.origin &&
      url.pathname === window.location.pathname &&
      url.hash.length > 1
    );
  } catch {
    return raw.startsWith('#') && raw.length > 1;
  }
}

/**
 * @param {string} hash
 */
function scrollToHashTarget(hash) {
  if (!hash || hash.length < 2) return;

  let id = hash.slice(1);
  try {
    id = decodeURIComponent(id);
  } catch {
    // keep raw id
  }

  const target = document.getElementById(id);
  if (!(target instanceof HTMLElement)) return;

  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * @param {HTMLDetailsElement} details
 * @param {HTMLElement} summary
 */
function closeDrawer(details, summary) {
  details.classList.remove('menu-open');
  summary.setAttribute('aria-expanded', 'false');
  clearPageScrollLock();

  const headerDrawer = details.closest('header-drawer');
  if (headerDrawer instanceof HTMLElement) {
    headerDrawer.querySelector('.menu-drawer')?.classList.remove('menu-drawer--has-submenu-opened');
  }

  const panel = details.querySelector('.menu-drawer');
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    details.removeAttribute('open');
    clearPageScrollLock();
  };

  if (panel instanceof HTMLElement) {
    panel.addEventListener('transitionend', finish, { once: true });
  }
  setTimeout(finish, 350);
}

/**
 * @param {HTMLDetailsElement} details
 */
function bindDrawer(details) {
  if (details.dataset.pbDrawerBound === 'true') return;
  details.dataset.pbDrawerBound = 'true';

  // Dropdown under sticky header — scroll-lock breaks sticky on mobile unlock
  details.removeAttribute('scroll-lock');

  const summary = details.querySelector('summary.header__icon--menu');
  if (!(summary instanceof HTMLElement)) return;

  summary.addEventListener(
    'click',
    (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();

      const isOpen = details.open || details.classList.contains('menu-open');

      if (isOpen) {
        closeDrawer(details, summary);
        return;
      }

      details.setAttribute('open', '');
      details.classList.add('menu-open');
      summary.setAttribute('aria-expanded', 'true');
      clearPageScrollLock();

      requestAnimationFrame(() => {
        restartAnimatedElements(details);
      });
    },
    true
  );

  details.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const anchor = event.target.closest('a[href]');
    if (!(anchor instanceof HTMLAnchorElement)) return;
    if (!isSameDocumentHashLink(anchor)) return;

    const isOpen = details.open || details.classList.contains('menu-open');
    if (!isOpen) return;

    const hash = (() => {
      try {
        return new URL(anchor.href, window.location.href).hash;
      } catch {
        return anchor.hash || '';
      }
    })();

    closeDrawer(details, summary);

    // Re-scroll after close so the target isn’t stuck behind the open drawer /
    // skipped when the URL already has this hash.
    window.setTimeout(() => {
      scrollToHashTarget(hash);
    }, 50);
  });
}

function init() {
  document.querySelectorAll('#Details-menu-drawer-container').forEach((node) => {
    if (node instanceof HTMLDetailsElement) bindDrawer(node);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', init);
